import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, Plug, PlugsConnected, X as XIcon, ArrowSquareOut, Key, CheckCircle } from "@phosphor-icons/react";
import client from "../lib/api";

export const GmailConnectCard = () => {
  const [gmail, setGmail] = useState(null);
  const [gmailBusy, setGmailBusy] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [connectForm, setConnectForm] = useState({ email: "", app_password: "" });

  const loadGmail = async () => {
    try {
      const { data } = await client.get("/settings/gmail-smtp");
      setGmail(data);
    } catch (e) {
      setGmail({ configured: false, connected: false });
    }
  };

  useEffect(() => { loadGmail(); }, []);

  const connectGmail = async (e) => {
    e?.preventDefault?.();
    if (!connectForm.email || !connectForm.app_password) {
      toast.error("Enter your Gmail and 16-character App Password");
      return;
    }
    setGmailBusy(true);
    try {
      const { data } = await client.post("/settings/gmail-smtp", {
        email: connectForm.email.trim().toLowerCase(),
        app_password: connectForm.app_password.replace(/\s+/g, ""),
      });
      toast.success(`✅ Gmail connected — broker emails will now send from ${data.email}`);
      setShowConnect(false);
      setConnectForm({ email: "", app_password: "" });
      await loadGmail();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gmail connect failed");
    } finally {
      setGmailBusy(false);
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm("Disconnect Gmail? Broker emails will fall back to sending from FreightPilot with your email as Reply-To.")) return;
    setGmailBusy(true);
    try {
      await client.delete("/settings/gmail-smtp");
      toast.success("Gmail disconnected");
      await loadGmail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setGmailBusy(false);
    }
  };

  return (
    <div className="card-surface p-6" data-testid="gmail-integration-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 mb-2">
            <EnvelopeSimple size={13} /> Broker Email — Sender
          </div>
          <h2 className="text-lg font-semibold text-white">
            {gmail?.connected ? (
              <span className="inline-flex items-center gap-2 flex-wrap">
                <PlugsConnected size={18} weight="fill" className="text-emerald-400" />
                Connected: <span className="font-mono text-emerald-400 break-all">{gmail.email || "your Gmail"}</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Plug size={18} className="text-neutral-400" />
                Send from your own Gmail
              </span>
            )}
          </h2>
          <p className="text-sm text-neutral-400 mt-2 leading-relaxed">
            {gmail?.connected ? (
              <>Broker emails now send directly from your Gmail via SMTP. Higher open rates, replies go to your inbox, emails appear in your Gmail "Sent" folder. Disconnect anytime.</>
            ) : (
              <>Connect your Gmail so broker negotiation emails send from <b>you</b>, not from FreightPilot. Uses Gmail's App Password (Google-supported, 2 min setup — no OAuth headaches).</>
            )}
          </p>
        </div>
        <div className="shrink-0">
          {gmail?.connected ? (
            <button
              onClick={disconnectGmail}
              disabled={gmailBusy}
              data-testid="gmail-disconnect-btn"
              className="rounded-full px-4 py-2.5 text-sm font-semibold border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5"
            >
              <XIcon size={13} /> {gmailBusy ? "…" : "Disconnect"}
            </button>
          ) : (
            <button
              onClick={() => setShowConnect((v) => !v)}
              data-testid="gmail-connect-toggle-btn"
              className="rounded-full px-5 py-2.5 text-sm font-semibold bg-white text-black hover:bg-neutral-200 transition-colors inline-flex items-center gap-2"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {showConnect ? "Cancel" : "Connect Gmail"}
            </button>
          )}
        </div>
      </div>

      {showConnect && !gmail?.connected && (
        <form onSubmit={connectGmail} className="mt-6 pt-6 border-t border-white/10 space-y-4" data-testid="gmail-connect-form">
          <div className="rounded-lg border border-[#007AFF]/30 bg-[#007AFF]/[0.06] p-4">
            <div className="text-xs uppercase tracking-widest text-[#007AFF] mb-2 font-semibold">Setup — 2 minutes</div>
            <ol className="text-xs text-neutral-300 space-y-1.5 list-decimal list-inside leading-relaxed">
              <li>Turn on 2-Step Verification (required by Google): <a href="https://myaccount.google.com/signinoptions/twosv" target="_blank" rel="noopener noreferrer" className="text-[#007AFF] hover:underline inline-flex items-center gap-1">Enable 2FA <ArrowSquareOut size={11} /></a></li>
              <li>Generate an App Password named "FreightPilot": <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-[#007AFF] hover:underline inline-flex items-center gap-1">Create App Password <ArrowSquareOut size={11} /></a></li>
              <li>Copy the 16-character password Google shows you and paste below.</li>
            </ol>
          </div>
          <div>
            <label className="block text-xs text-neutral-400 mb-1">Your Gmail address</label>
            <div className="relative">
              <EnvelopeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                type="email"
                required
                autoFocus
                value={connectForm.email}
                onChange={(e) => setConnectForm({ ...connectForm, email: e.target.value })}
                placeholder="you@gmail.com"
                data-testid="gmail-connect-email"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 pl-9 pr-3 focus:outline-none focus:border-[#FF5A1F]/60"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-neutral-400 mb-1">App Password (16 characters — spaces OK)</label>
            <div className="relative">
              <Key size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                type="text"
                required
                value={connectForm.app_password}
                onChange={(e) => setConnectForm({ ...connectForm, app_password: e.target.value })}
                placeholder="abcd efgh ijkl mnop"
                data-testid="gmail-connect-password"
                autoComplete="off"
                spellCheck="false"
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg text-white text-sm py-2.5 pl-9 pr-3 focus:outline-none focus:border-[#FF5A1F]/60 font-mono tracking-wide"
              />
            </div>
            <p className="text-[11px] text-neutral-500 mt-1">Google will show this as 4 groups of 4 letters. You can paste with or without spaces.</p>
          </div>
          <div className="flex items-center justify-between pt-2">
            <p className="text-[11px] text-neutral-500 leading-relaxed max-w-md">
              🔒 Your app password is encrypted with AES before storing. We only use it to send broker emails on your behalf.
            </p>
            <button
              type="submit"
              disabled={gmailBusy}
              data-testid="gmail-connect-submit"
              className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 transition-colors inline-flex items-center gap-2 shrink-0"
            >
              {gmailBusy ? (<>Verifying…</>) : (<><CheckCircle size={13} weight="fill" /> Connect</>)}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default GmailConnectCard;
